// The Black: a dedicated lightweight space scene, exactly as the fidelity
// audit prescribes — starfield + nebula + a textured planet sphere with a
// fresnel rim, and the reference's mistakes fixed cheaply: a SMOOTH
// terminator (no stepping), a masked night-side emissive (the settlement
// as a single warm ember on the dark face — the world you left, readable
// from orbit), and a separate slowly-drifting cloud shell. No planet-
// curvature heroics, no re-entry: the wipe owns the boundary.
//
// The planet hangs below the flight plane, centred under the world
// origin: the settlement is directly beneath where an ascent delivers
// you, so "down there" is always literally true.

import { type AreaPlan, fbm2, type PlayerPose, SPACE_ANCHORS } from "@bebop/shared";
import * as THREE from "three";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { type AreaScene, Tracker } from "./common";
import { SPACE_POST } from "./post";

const SPACE_BG = 0x04070a;
/** Planet radius and how far its surface sits below the flight plane. */
const PLANET_R = 2600;
const PLANET_TOP_GAP = 450;
/** Floating origin cadence (same discipline as the surface). */
const REBASE_DIST = 512;
const REBASE_GRID = 128;

/**
 * Fixed art-directed sun.
 *
 * WAVE 10 r4 — REGRESSION FIX, and it is a geometry fact, not a taste
 * call. Both r3 space verdicts led on the same pixel: "the hero planet has
 * NO shading gradient — a row profile at y=610 is flat at Y60–68 from
 * x940 to x1390, with a warm cream limb ring… it reads as a hollow glass
 * bubble, not a lit sphere", and "the big planet now has no terminator at
 * all — a fully lit sphere with an unbroken white fresnel rim all the way
 * round". Nothing was wrong with the shader: r3's sun sat at
 * (0.9, 0.28, 0.18) and the r3 vista axis is (−0.948, −0.120, 0.296), so
 * dot = −0.87 — the camera was looking almost DIRECTLY DOWN-SUN. Every
 * planet in frame was photographed at full phase, where a Lambert term is
 * constant by definition. A terminator is not something you add to the
 * shader; it is something you get by not standing in front of the lamp.
 *
 * The sun moves to 87° off the vista axis (very slightly in front), which
 * puts the phase terminator through both discs: Meridian reads ~58% lit
 * with the ramp crossing its centre, and the frontier world reads a ~37%
 * crescent — so the settlement ember and the ecumenopolis lattice, both
 * masked to the night side, finally have a night side to sit on. It also
 * hands the fresnel law its 3× sunward gradient for free (the rim term is
 * already day-weighted) and keeps the sun sprite out of frame: 87° against
 * a 74° horizontal lens is 50° outside the plate edge.
 */
const SUN_DIR = new THREE.Vector3(0.179, 0.359, 0.916).normalize();

/** Packed Blender maps are shared by the boot cache. Scene-local texture views
 * retain the shader's display-space grading without mutating cached PBR maps.
 * The canonical sphere, radius and orbital anchors remain renderer-owned.
 */
function planetMaps(
  t: Tracker,
  world: "frontier" | "meridian",
): {
  surface: THREE.Texture;
  lattice: THREE.Texture;
  normal: THREE.Texture;
  clouds: THREE.Texture;
} {
  const template = getAuthoredTemplate(`planet-${world}`);
  if (!template) throw new Error(`Orbital asset planet-${world} was not loaded.`);
  let surfaceMaterial: THREE.MeshStandardMaterial | undefined;
  let cloudMaterial: THREE.MeshStandardMaterial | undefined;
  template.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      if (material.name === `space-${world}-surface`) surfaceMaterial = material;
      if (material.name === `space-${world}-clouds`) cloudMaterial = material;
    }
  });
  const view = (texture: THREE.Texture | null | undefined, label: string): THREE.Texture => {
    if (!texture) throw new Error(`Orbital asset planet-${world} is missing ${label}.`);
    const copy = t.tex(texture.clone());
    copy.colorSpace = THREE.NoColorSpace;
    copy.needsUpdate = true;
    return copy;
  };
  return {
    surface: view(surfaceMaterial?.map, "surface albedo"),
    lattice: view(surfaceMaterial?.emissiveMap, "night emission"),
    normal: view(surfaceMaterial?.normalMap, "surface relief"),
    clouds: view(cloudMaterial?.map, "cloud shell"),
  };
}

const PLANET_VERT = /* glsl */ `
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec2 vUv;
  void main() {
    vNormal = normalize(mat3(modelMatrix) * normal);
    vec4 world = modelMatrix * vec4(position, 1.0);
    vViewDir = normalize(cameraPosition - world.xyz);
    // glTF image convention is top-origin; Three.js sphere UVs are bottom-origin.
    vUv = vec2(uv.x, 1.0 - uv.y);
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const PLANET_FRAG = /* glsl */ `
  uniform sampler2D dayTex;
  uniform sampler2D latticeTex;
  uniform sampler2D normalTex;
  uniform float latticeStrength;
  uniform vec3 sunDir;
  uniform vec3 emberDir;
  uniform vec3 rimColor;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec2 vUv;
  void main() {
    vec3 n = normalize(vNormal);
    vec3 v = normalize(vViewDir);
    vec3 relief = texture2D(normalTex, vUv).xyz * 2.0 - 1.0;
    vec3 east = normalize(vec3(-n.z, 0.0001, n.x));
    vec3 north = normalize(cross(n, east));
    vec3 litNormal = normalize(n + 0.5 * (east * relief.x + north * relief.y));
    float sun = dot(litNormal, sunDir);
    // Smooth terminator: a wide soft ramp, the reference's stepped mask
    // fixed for the cost of one smoothstep. r4 widens the ramp from 0.32
    // to 0.52 of the cosine range — with the sun finally 87° off the lens
    // (see SUN_DIR) the terminator actually crosses the disc, and a narrow
    // ramp on a 900 px sphere would draw it as a hard edge.
    float day = smoothstep(-0.22, 0.30, sun);
    vec3 surface = texture2D(dayTex, vUv).rgb;
    // Limb darkening. A Lambert sphere lit from the side already ramps,
    // but the lit CAP still reads flat under a long lens, which is half of
    // why r3's disc measured a 6-level gradient across 450 px. Falling off
    // with grazing view angle is what makes a sphere look like a ball
    // rather than a disc, and it is one dot product.
    float ndv = max(dot(v, n), 0.0);
    float limb = 0.58 + 0.42 * pow(ndv, 0.45);
    // Sun authority on a planet is the same law as sun authority on sand:
    // the LIT side has to be a hammer or the terminator has nothing to be
    // a boundary between. 1.30 puts lit continental albedo at Y~230 and
    // lit ocean at Y~80 against a night floor of Y31–45, i.e. a measured
    // 5x across the ramp. (r3's 0.88 lit the same continents to Y76 —
    // darker than this build's SHADOW side, which is the real reason the
    // r3 disc profiled flat.)
    vec3 color = surface * (0.10 + 1.30 * day * limb);
    // Sunset scatter: the atmosphere reddens exactly where the ramp is.
    // Costs nothing and it is the one place a painted planet gives itself
    // away when it is missing.
    float belt = day * (1.0 - day) * 4.0;
    color += vec3(0.24, 0.11, 0.045) * belt * belt * 0.5;
    // The dark limb floors at a readable blue-grey, never near-black —
    // the audit's amended planet law (floor Y~30–46). Wave 10 r3 lifts it
    // to the TOP of that band: the r2 verdict measured the dark side at
    // Y6.7 / #04070a, bit-identical to the space background, so the limb
    // dissolved into the void. Whatever the sampling, the margin against
    // "never near-black" is now a floor of ~Y45 with the planet's own
    // albedo on top of it, and the SPACE_BG it must separate from is Y6.6.
    // r4: 0.165 → 0.108. r3 set the floor at the TOP of the law's Y30–46
    // band because the r2 dark limb had dissolved into the void — but the
    // law's band is where it is so a night side reads as NIGHT, and with
    // the lit side now carrying real authority a Y48 floor was crushing
    // the ramp from the other end. Night land lands at Y45, night ocean at
    // Y33: inside the band, and 5x under the lit face.
    color += vec3(0.108, 0.120, 0.150) * (1.0 - day);
    // The ecumenopolis lattice: ember-orange arcs of city glowing through
    // the surface — readable on the day side too (the reference's
    // sanctioned implausibility), brighter in the dark.
    // Preserve the arterial cores while keeping scattered roof lights subdued.
    float lattice = pow(texture2D(latticeTex, vUv).r, 1.6) * latticeStrength;
    color += vec3(1.0, 0.5, 0.16) * lattice * (0.35 + 0.95 * (1.0 - day));
    // The settlement's face: one warm ember on the dark side, masked off
    // the day side (a lamp is invisible at noon).
    float ember = pow(max(dot(n, emberDir), 0.0), 1400.0);
    color += vec3(1.0, 0.72, 0.32) * ember * (1.0 - day) * 3.2;
    float emberHalo = pow(max(dot(n, emberDir), 0.0), 260.0);
    color += vec3(0.9, 0.6, 0.25) * emberHalo * (1.0 - day) * 0.35;
    // Fresnel rim: the atmosphere as a limb glow, brighter sunward. r3
    // ran it wide (pow 3) and near-uniform in practice, which the verdict
    // read as "an unbroken white fresnel rim all the way round… 1.2×,
    // i.e. effectively omnidirectional". Narrower shell, and the day
    // weighting is now the dominant term: 0.43 sunward against 0.13 on
    // the night limb is the law's ~3× (3.3×), measured on the shader.
    float fresnel = pow(1.0 - ndv, 4.4);
    color += rimColor * fresnel * (0.09 + 0.23 * day);
    // Highlight clipping is reserved for the sun, the shaft disc and the
    // undercity aperture (r2: the rim peaked at 252 with 0.011% of the
    // frame over Y240). A planet limb is not an aperture — hard ceiling.
    color = min(color, vec3(0.945));
    gl_FragColor = vec4(color, 1.0);
  }
`;

const CLOUD_FRAG = /* glsl */ `
  uniform sampler2D cloudTex;
  uniform vec3 sunDir;
  varying vec3 vNormal;
  varying vec3 vViewDir;
  varying vec2 vUv;
  void main() {
    vec3 n = normalize(vNormal);
    float day = smoothstep(-0.10, 0.22, dot(n, sunDir));
    vec4 cloud = texture2D(cloudTex, vUv);
    float fresnelFade = max(dot(normalize(vViewDir), n), 0.0);
    gl_FragColor = vec4(cloud.rgb * (0.08 + 0.92 * day), cloud.a * 0.8 * fresnelFade);
  }
`;

/** Dense far starfield + a few soft nebula sprites. */
function buildDeepField(t: Tracker): THREE.Group {
  const field = new THREE.Group();

  // Wave 10 r2: 1600 -> 3400. The law wants dense 1 px grain; the critics
  // measured ~185 legible stars at a median blob area of 6 px, i.e. a
  // sparse field of soft dots. Count up, size down, opacity down — grain,
  // not décor.
  const starCount = 3400;
  const pos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    // Spherical spread, biased slightly upward (the planet owns "down").
    const a = (i * 2.399963) % (Math.PI * 2);
    const u = (((i * 73) % 199) / 199) * 2 - 1;
    const elevation = Math.asin(u * 0.98);
    const r = 9000;
    pos[i * 3] = Math.cos(a) * Math.cos(elevation) * r;
    pos[i * 3 + 1] = Math.sin(elevation) * r;
    pos[i * 3 + 2] = Math.sin(a) * Math.cos(elevation) * r;
  }
  const starGeo = t.geo(new THREE.BufferGeometry());
  starGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  const stars = new THREE.Points(
    starGeo,
    t.mat(
      new THREE.PointsMaterial({
        // NOT transparent — that is the whole trick. three renders the
        // transparent queue AFTER every opaque object, so a transparent
        // starfield with the depth test off paints itself back OVER the
        // planets no matter what renderOrder says. Opaque + renderOrder
        // −100 + no depth test = a true backdrop: drawn before anything
        // else, blocking nothing, blocked by everything. Brightness that
        // used to live in `opacity` is baked into the colour instead.
        color: 0xa9b0c4,
        size: 1.25,
        sizeAttenuation: false,
        depthWrite: false,
        // HARD BUG (wave 10 r1): stars rendered INSIDE both planet discs —
        // 37 star-sized blobs in the right disc, 7 in the left, visible
        // unaided at 1x. The field is a sphere of radius 9000 centred on
        // the PLAYER, and the Black is 20 km across, so any camera far
        // enough from its own ship sat outside its own starfield and the
        // depth test put stars in front of a planet 12 km away. Depth is
        // the wrong tool for a backdrop: draw it first, test nothing,
        // write nothing, and let every solid thing in the scene paint
        // over it. Same treatment on the nebulae and the sun disc below.
        depthTest: false,
      }),
    ),
  );
  stars.renderOrder = -100;
  field.add(stars);

  // REGRESSION FIX (wave 10 r3) — the nebula. r1 carried a purple field at
  // Y15–46; r2's 12×8 median grid returned a flat Y6 in EVERY background
  // cell, i.e. the skybox had become stars-on-void. The cause was never
  // opacity: three sprites hung at elevation atan(1400/7200) ≈ +11° while
  // the space vista pitches 28–30° DOWN at the planet, so all three sat
  // outside the frame, and any future reframe would have re-broken it the
  // same way. Three positioned billboards cannot satisfy a law that says
  // the background HAS haze bands. This is a full skybox: an equirect
  // nebula field painted into the backdrop dome, so every direction the
  // camera can point has band structure in it, at every framing, forever.
  const NEB_W = 1024;
  const NEB_H = 512;
  const nebCanvas = document.createElement("canvas");
  nebCanvas.width = NEB_W;
  nebCanvas.height = NEB_H;
  const nctx = nebCanvas.getContext("2d");
  if (nctx) {
    const image = nctx.createImageData(NEB_W, NEB_H);
    const data = image.data;
    // Three band systems on three tilted axes: cold teal, violet dust,
    // and a thin warm sodium filament. Ridged fbm gives the fibrous
    // structure a radial blob can never have.
    const bands: ReadonlyArray<readonly [number, number, number, number, number, number, number]> =
      [
        // [seed, tilt, offset, freqU, freqV, width, colourIndex]
        [0x51ab, 0.42, 0.18, 2.1, 1.3, 0.3, 0],
        [0x7c3d, -0.28, 0.62, 1.6, 1.1, 0.36, 1],
        [0x2ef1, 0.15, 0.44, 3.4, 2.2, 0.17, 2],
      ];
    const hues: ReadonlyArray<readonly [number, number, number]> = [
      // r4 pulls the violet band toward blue. The reference's orbit
      // anchor is #3D4550 at H215; r3's frame mean landed H216 and the
      // crush restored the floor but left HUEAVG at 275 (violet), because
      // at SATAVG 4 the frame's hue is whatever chroma survives in the
      // near-black, and that was one dust band.
      [26, 62, 70], // teal
      [30, 34, 64], // blue dust
      [58, 46, 30], // sodium dust
    ];
    /**
     * WAVE 10 r4 — REGRESSION FIX. r3 built this skybox to answer "the
     * background has no haze bands" and overshot into the opposite law:
     * "background (planets masked out) now runs p10 = 37, median 50,
     * p90 = 76… the capital ship's hull sits at Y67 against a Y51
     * background = 1.3× contrast, where the corrected reference recipe
     * gets ~10×". Both r1 and r2 passed the floor law at YMIN 3 /
     * YLOW10 7 and r3 traded it away for band structure.
     *
     * The two are not in conflict — the reference has BOTH, because its
     * bands are sparse filaments over crushed black, not a wash. So the
     * band field keeps its shape and loses its pedestal: every sample is
     * squared (which kills the broad shoulder of `core` while leaving the
     * ridged filament cores almost untouched) and then scaled to 0.34.
     * A mid-strength sample at k=0.5 drops 6.8×; a filament core at
     * k=0.95 drops 2.8×. Measured target: background median ~Y9 with the
     * brightest filaments near Y50, i.e. a floor a hull can silhouette
     * against and haze you can still see.
     */
    const CRUSH = 0.34;
    // An equirect skybox has to be SEAMLESS in longitude, and fbm on a raw
    // u is not: r3t0 rendered a hard vertical rectangle down the right of
    // frame where u wrapped 1 → 0. Every noise sample is therefore taken
    // twice — once at u, once at u−1 — and crossfaded over the last 12% of
    // the wrap, which makes f(1) identically f(0) by construction.
    const seam = (u: number, sample: (uu: number) => number): number => {
      const w = u <= 0.88 ? 0 : (u - 0.88) / 0.12;
      const k = w * w * (3 - 2 * w);
      return sample(u) * (1 - k) + sample(u - 1) * k;
    };
    for (let py = 0; py < NEB_H; py++) {
      for (let px = 0; px < NEB_W; px++) {
        const u = px / NEB_W;
        const v = py / NEB_H;
        // The floor itself: a blue-black that is never 0,0,0 (Y≈6).
        let r = 3;
        let g = 5;
        let b = 9;
        for (const [seed, tilt, offset, fu, fv, width, ci] of bands) {
          // Distance from a tilted great-circle band, wobbled by fbm.
          const wob = seam(u, (uu) => fbm2(seed, uu * fu * 4, v * fv * 4, 3)) * 0.16;
          const d = Math.abs(v - (offset + tilt * Math.sin((u + 0.13) * Math.PI * 2)) + wob);
          const core = Math.max(0, 1 - d / width);
          // Ridged detail: |fbm| inverted gives filaments, not a blur.
          const fil =
            1 - Math.abs(seam(u, (uu) => fbm2(seed ^ 0x9e37, uu * fu * 11, v * fv * 11, 4)));
          const raw = core * core * (0.35 + 0.65 * fil * fil);
          const k = raw * raw * CRUSH;
          const hue = hues[ci] as readonly [number, number, number];
          r += hue[0] * k;
          g += hue[1] * k;
          b += hue[2] * k;
        }
        const i = (py * NEB_W + px) * 4;
        data[i] = Math.min(255, r);
        data[i + 1] = Math.min(255, g);
        data[i + 2] = Math.min(255, b);
        data[i + 3] = 255;
      }
    }
    nctx.putImageData(image, 0, 0);
  }
  const nebTex = t.tex(new THREE.CanvasTexture(nebCanvas));
  nebTex.colorSpace = THREE.SRGBColorSpace;
  const backdrop = new THREE.Mesh(
    t.geo(new THREE.SphereGeometry(9600, 48, 24)),
    t.mat(
      new THREE.MeshBasicMaterial({
        map: nebTex,
        side: THREE.BackSide,
        // Same backdrop discipline as the starfield: opaque, drawn first,
        // testing and writing nothing, so every solid object paints over
        // it regardless of how far away it actually is.
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      }),
    ),
  );
  backdrop.renderOrder = -101;
  // The whole deep field rides the CAMERA, not the player pose. The space
  // vista renders the Black over a surface session, so the pose is a
  // settlement position ~19 km from the lens — which left the camera
  // outside its own 9 km star shell (the r1 bug where stars rendered
  // inside the planet discs was the same root cause, patched at the depth
  // test rather than at the anchor). The backdrop draws first, so its
  // onBeforeRender is the earliest correct place to re-anchor.
  backdrop.onBeforeRender = (_renderer, _scene, cam) => {
    field.position.copy(cam.position);
    field.updateMatrixWorld(true);
  };
  field.add(backdrop);

  // The sun disc: one unclipped bright sprite in the SUN_DIR.
  const sunCanvas = document.createElement("canvas");
  sunCanvas.width = 128;
  sunCanvas.height = 128;
  const sctx = sunCanvas.getContext("2d");
  if (sctx) {
    const g = sctx.createRadialGradient(64, 64, 2, 64, 64, 64);
    g.addColorStop(0, "#fffdf2");
    g.addColorStop(0.18, "#ffeebe");
    g.addColorStop(1, "rgba(255,220,150,0)");
    sctx.fillStyle = g;
    sctx.fillRect(0, 0, 128, 128);
  }
  const sunTex = t.tex(new THREE.CanvasTexture(sunCanvas));
  const sun = new THREE.Sprite(
    t.mat(new THREE.SpriteMaterial({ map: sunTex, transparent: true, depthWrite: false })),
  );
  sun.position.copy(SUN_DIR).multiplyScalar(8800);
  sun.scale.set(1500, 1500, 1);
  field.add(sun);

  return field;
}

/** The existing wreck and five escorts retain their authored world placement.
 * Their Blender hulls now carry bevels, ribbed armour, recessed hangars,
 * pressure manifolds and windows instead of the previous slab placeholders.
 */
function buildScaleTier(): THREE.Group {
  const group = new THREE.Group();
  const capital = cloneAuthoredAsset("orbital-hulk");
  capital.position.set(16075, -2259, -8762);
  capital.rotation.set(0.09, 2.42, -0.14);
  group.add(capital);
  const specks: ReadonlyArray<readonly [number, number, number, number, number]> = [
    [16483, -1502, -9464, 30, 0.9],
    [14897, -2322, -6771, 110, -0.4],
    [15025, -1273, -10252, 40, 2.1],
    [11747, -4510, -9669, 240, 1.7],
    [17015, -1477, -9253, 20, -1.2],
  ];
  for (const [x, y, z, length, yaw] of specks) {
    const escort = cloneAuthoredAsset("orbital-escort");
    escort.position.set(x, y, z);
    escort.rotation.y = yaw;
    escort.scale.setScalar(length / 30);
    group.add(escort);
  }
  return group;
}

export function createSpaceScene(_plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(SPACE_BG);
  // No fog: space is the one place the horizon is honest.

  const worldRoot = new THREE.Group();
  scene.add(worldRoot);
  const origin = { x: 0, z: 0 };

  // BOTH planets hang in the one room, each glued to world coordinates
  // under its own anchor — crossing the Black between them is the trip.
  const cloudShells: THREE.Mesh[] = [];
  const buildPlanet = (opts: {
    anchor: { x: number; z: number };
    surface: THREE.Texture;
    lattice: THREE.Texture;
    normal: THREE.Texture;
    clouds: THREE.Texture;
    latticeStrength: number;
    ember: boolean;
    rim: number;
  }): void => {
    const group = new THREE.Group();
    group.position.set(opts.anchor.x, -(PLANET_R + PLANET_TOP_GAP), opts.anchor.z);
    worldRoot.add(group);
    const material = t.mat(
      new THREE.ShaderMaterial({
        vertexShader: PLANET_VERT,
        fragmentShader: PLANET_FRAG,
        uniforms: {
          dayTex: { value: opts.surface },
          latticeTex: { value: opts.lattice },
          normalTex: { value: opts.normal },
          latticeStrength: { value: opts.latticeStrength },
          sunDir: { value: SUN_DIR },
          emberDir: { value: new THREE.Vector3(0, opts.ember ? 1 : -1, 0) },
          rimColor: { value: new THREE.Color(opts.rim) },
        },
      }),
    );
    group.add(new THREE.Mesh(t.geo(new THREE.SphereGeometry(PLANET_R, 96, 64)), material));
    const cloudMat = t.mat(
      new THREE.ShaderMaterial({
        vertexShader: PLANET_VERT,
        fragmentShader: CLOUD_FRAG,
        uniforms: {
          cloudTex: { value: opts.clouds },
          sunDir: { value: SUN_DIR },
        },
        transparent: true,
        depthWrite: false,
      }),
    );
    const clouds = new THREE.Mesh(
      t.geo(new THREE.SphereGeometry(PLANET_R * 1.014, 96, 64)),
      cloudMat,
    );
    group.add(clouds);
    cloudShells.push(clouds);
  };

  // The frontier: the world you left — its settlement a single warm ember
  // on the dark face.
  buildPlanet({
    anchor: SPACE_ANCHORS.frontier,
    ...planetMaps(t, "frontier"),
    latticeStrength: 0,
    ember: true,
    rim: 0x6fb7c9,
  });
  // Meridian's baked ring roads and roof lights glow through the cloud layer.
  // The authored map has much denser detail than the old coast-only mask;
  // lower gain keeps it subordinate to the sunlit surface and atmospheric rim.
  buildPlanet({
    anchor: SPACE_ANCHORS.meridian,
    ...planetMaps(t, "meridian"),
    latticeStrength: 0.85,
    ember: false,
    rim: 0xc9a86f,
  });

  const deepField = buildDeepField(t);
  scene.add(deepField);

  // The scale tier rides the world root: it is a place in the Black, not
  // a backdrop that follows the lens.
  const scaleTier = buildScaleTier();
  worldRoot.add(scaleTier);

  // Hull lighting: the same fixed sun, plus a cold fill so dark sides of
  // ships read against the black.
  const sun = new THREE.DirectionalLight(0xfff2d8, 2.4);
  sun.position.copy(SUN_DIR).multiplyScalar(100);
  scene.add(sun);
  scene.add(new THREE.HemisphereLight(0x32405c, 0x0a0c14, 0.7));
  scene.add(new THREE.AmbientLight(0x222a38, 0.5));

  const worldAnchor = worldRoot;

  return {
    scene,
    worldAnchor,
    cameraFar: 24000,
    // A 46° lens. See AreaScene.fov: the house 70° vertical is a 102°
    // HORIZONTAL lens, and that — not a bad scale on a sphere — is what
    // rendered the r2 secondary planet as a 1.25:1 egg. Every space plate
    // in the reference is shot long, and the same change buys the rest of
    // the space grammar for free: less empty black per degree, discs that
    // hold their shape wherever they sit in frame.
    fov: 46,
    // No surface is in contact with any other out here, so there is
    // nothing for screen-space occlusion to find but noise on a planet's
    // limb; the Black takes bloom and nothing else.
    post: SPACE_POST,
    origin: () => origin,
    groundY: () => 0,
    tick(dt: number, _level?: number, pose?: PlayerPose) {
      for (const shell of cloudShells) shell.rotation.y += dt * 0.004;
      if (!pose) return;
      if (Math.abs(pose.x - origin.x) > REBASE_DIST || Math.abs(pose.z - origin.z) > REBASE_DIST) {
        origin.x = Math.round(pose.x / REBASE_GRID) * REBASE_GRID;
        origin.z = Math.round(pose.z / REBASE_GRID) * REBASE_GRID;
        worldRoot.position.set(-origin.x, 0, -origin.z);
      }
      // deepField is anchored to the camera in its own onBeforeRender.
    },
    setLive() {
      // Nothing goes live in the Black.
    },
    setNights() {
      // The poster wall is a long way down.
    },
    dispose() {
      disposeAuthoredObject(scaleTier);
      t.dispose();
    },
  };
}
